import type {
  CustomAbutmentCharacteristics,
  ImplantCharacteristics,
  ImplantedItemCharacteristics,
  StandardAbutmentCharacteristics,
} from "../implants";
import type {
  MaterialCharacteristics,
  MaterialCharacteristicsWithoutShade,
} from "../material";
import type { TeethArch } from "../teeth";
import type { WaxUpType } from "./waxUp.types";

export const ItemTypeEnum = {
  bridge: "BRIDGE",
  crown: "CROWN",
  lay: "LAY",
  inlay: "INLAY",
  onlay: "ONLAY",
  overlay: "OVERLAY",
  inlayCore: "INLAY_CORE",
  postAndCoreRetainedCrown: "POST_AND_CORE_RETAINED_CROWN",
  veneer: "VENEER",
  implant: "IMPLANT",
  implantCrown: "IMPLANT_CROWN",
  abutment: "ABUTMENT",
  customAbutment: "CUSTOM_ABUTMENT",
  splint: "SPLINT",
  nightGuard: "NIGHT_GUARD",
  bleachingTray: "BLEACHING_TRAY",
  retainer: "RETAINER",
  removableDenture: "REMOVABLE_DENTURE",
  partialDenture: "PARTIAL_DENTURE",
  fullDenture: "FULL_DENTURE",
  fullDentureWaxBite: "FULL_DENTURE_WAX_BITE",
  immediateDenture: "IMMEDIATE_DENTURE",
  model: "MODEL",
  richmondCrown: "RICHMOND_CROWN",
  splintedCrown: "SPLINTED_CROWN",
  splintedCrownImplant: "SPLINTED_CROWN_IMPLANT",
  bridgeImplant: "BRIDGE_IMPLANT",
  bridgePontic: "BRIDGE_PONTIC",
  scanOnly: "SCAN_ONLY",
  spaceMaintainer: "SPACE_MAINTAINER",
  retentionWire: "RETENTION_WIRE",
  waxUp: "WAX_UP",
  waxBite: "WAX_BITE",
  additionalImpressionForRemovableProsthesis:
    "ADDITIONAL_IMPRESSION_FOR_REMOVABLE_PROSTHESIS",
  addingTeethOnExistingDenture: "ADDING_TEETH_ON_EXISTING_DENTURE",
  dentureRepair: "DENTURE_REPAIR",
  orthoModel: "ORTHO_MODEL",
  customTray: "CUSTOM_TRAY",
  metalMesh: "METAL_MESH",
} as const;

export type ItemType = (typeof ItemTypeEnum)[keyof typeof ItemTypeEnum];
export const SOLIDARIZED_PRODUCT_TYPE: Array<ItemType> = [
  ItemTypeEnum.bridge,
  ItemTypeEnum.splintedCrown,
  ItemTypeEnum.splintedCrownImplant,
] as const;

export interface InlayCoreCharacteristics {
  hasClavette: boolean;
  scanPost: string;
}

export const ItemScopeEnum = {
  tooth: "TOOTH",
  arch: "ARCH",
} as const;

export type ItemScope = (typeof ItemScopeEnum)[keyof typeof ItemScopeEnum];

/**
 * `tooth` and `arch` are declared on the base so a generic `Item` can be read without
 * narrowing first; each variant makes its own field required. `scope` is the
 * discriminant and stays optional on the tooth variant, which is the default.
 */
interface ItemBase {
  id: string;
  type: ItemType;
  scope?: ItemScope;
  tooth?: number;
  arch?: TeethArch;
  materialCharacteristics?: MaterialCharacteristics;
  inlayCoreCharacteristics?: InlayCoreCharacteristics | null;
}

export interface ToothScopedItem extends ItemBase {
  scope?: typeof ItemScopeEnum.tooth;
  tooth: number;
  materialCharacteristics?: MaterialCharacteristics;
  waxUpType?: WaxUpType;
}

export interface ArchScopedItem extends ItemBase {
  scope: typeof ItemScopeEnum.arch;
  arch: TeethArch;
}

export type Item = ToothScopedItem | ArchScopedItem;

export const isArchScopedItem = <T extends { scope?: ItemScope | null }>(
  item: T
): item is T & { scope: typeof ItemScopeEnum.arch; arch: TeethArch } =>
  item.scope === ItemScopeEnum.arch;

export const isToothScopedItem = (item: Item): item is ToothScopedItem =>
  !isArchScopedItem(item);

export interface BridgeItem extends ToothScopedItem {
  type: typeof ItemTypeEnum.bridge;
  materialCharacteristics: MaterialCharacteristics;
}

export interface VeneerItem extends ToothScopedItem {
  type: typeof ItemTypeEnum.veneer;
  materialCharacteristics: MaterialCharacteristics;
}

export interface LayItem extends ToothScopedItem {
  type: typeof ItemTypeEnum.lay;
  materialCharacteristics: MaterialCharacteristics;
}

export interface CrownItem extends ToothScopedItem {
  type: typeof ItemTypeEnum.crown;
  materialCharacteristics: MaterialCharacteristics;
}

export interface AddingTeethOnExistingDentureItem extends ToothScopedItem {
  type: typeof ItemTypeEnum.addingTeethOnExistingDenture;
}

export interface SplintedCrownItem extends ToothScopedItem {
  type: typeof ItemTypeEnum.splintedCrown;
  materialCharacteristics: MaterialCharacteristics;
}

export interface WaxBiteItem extends ArchScopedItem {
  type: typeof ItemTypeEnum.waxBite;
}

export interface RetentionWireItem extends ArchScopedItem {
  type: typeof ItemTypeEnum.retentionWire;
}

export interface CustomTrayItem extends ArchScopedItem {
  type: typeof ItemTypeEnum.customTray;
}

export interface InlayCoreItem extends ToothScopedItem {
  type: typeof ItemTypeEnum.inlayCore;
  materialCharacteristics: MaterialCharacteristicsWithoutShade;
  inlayCoreCharacteristics: InlayCoreCharacteristics;
}

export type InlayCoreConfiguration = Pick<
  InlayCoreItem,
  "materialCharacteristics" | "inlayCoreCharacteristics"
>;

export interface SpaceMaintainerItem extends ToothScopedItem {
  type: typeof ItemTypeEnum.spaceMaintainer;
  materialCharacteristics: undefined;
}

export interface OrthoModelItem extends ArchScopedItem {
  type: typeof ItemTypeEnum.orthoModel;
}

export interface AdditionalImpressionForRemovableProsthesisItem extends ArchScopedItem {
  type: typeof ItemTypeEnum.additionalImpressionForRemovableProsthesis;
}

export interface DentureRepairItem extends ArchScopedItem {
  type: typeof ItemTypeEnum.dentureRepair;
}
export interface ImplantCrownItem extends ToothScopedItem {
  type: typeof ItemTypeEnum.implantCrown;
  materialCharacteristics: MaterialCharacteristics;
  implantedItemCharacteristics: ImplantedItemCharacteristics;
}

export interface CustomAbutmentItem extends ToothScopedItem {
  type: typeof ItemTypeEnum.customAbutment;
  materialCharacteristics: undefined;
  implantCharacteristics: ImplantCharacteristics;
  abutmentCharacteristics: CustomAbutmentCharacteristics;
}

export interface StandardAbutmentItem extends ToothScopedItem {
  type: typeof ItemTypeEnum.abutment;
  materialCharacteristics: undefined;
  implantCharacteristics: ImplantCharacteristics;
  abutmentCharacteristics: StandardAbutmentCharacteristics;
}

export type AbutmentItem = CustomAbutmentItem | StandardAbutmentItem;

export interface ImplantBridgeItem extends ToothScopedItem {
  type: typeof ItemTypeEnum.bridgeImplant;
  materialCharacteristics: MaterialCharacteristics;
  implantedItemCharacteristics: ImplantedItemCharacteristics;
}

export interface BridgePonticItem extends ToothScopedItem {
  type: typeof ItemTypeEnum.bridgePontic;
  materialCharacteristics: MaterialCharacteristics;
}
export interface NightGuardItem extends ArchScopedItem {
  type: typeof ItemTypeEnum.nightGuard;
  materialCharacteristics: MaterialCharacteristicsWithoutShade;
}

export interface BleachingTrayItem extends ArchScopedItem {
  type: typeof ItemTypeEnum.bleachingTray;
}

export interface RetainerItem extends ArchScopedItem {
  type: typeof ItemTypeEnum.retainer;
}
